import React, {
  cloneElement,
  FC,
  HTMLAttributes,
  isValidElement,
  ReactElement,
} from 'react';

import classNames from 'classnames';

import styles from './menu-item.module.scss';

interface IMenuItemProps extends Omit<HTMLAttributes<HTMLLIElement>, 'onClick'> {
  align?: 'left' | 'center' | 'right';
  isDisable?: boolean;
  onClick?: () => void;
}

const MenuItem: FC<IMenuItemProps> = ({
  align = 'left',
  isDisable,
  children,
  onClick,
  className,
  'aria-label': ariaLabel,
  ...props
}): ReactElement => {
  const hasInteractiveChild = React.Children.toArray(children).some(
    child =>
      isValidElement(child) &&
      (child.type === 'button' ||
        child.type === 'a' ||
        child.props.to ||
        child.props.href ||
        child.props.role === 'button'),
  );
  return (
    <li
      role="none"
      className={classNames(styles['menu-item'], styles[align], className, {
        [styles.disabled]: isDisable,
      })}
      {...props}
      onClickCapture={event => {
        if (isDisable) {
          event.preventDefault();
          event.stopPropagation();
        } else props.onClickCapture?.(event);
      }}
      onClick={hasInteractiveChild && !isDisable ? onClick : undefined}
    >
      {onClick && !hasInteractiveChild ? (
        <button
          type="button"
          role="menuitem"
          aria-label={ariaLabel}
          className={styles.action}
          disabled={isDisable}
          onClick={onClick}
        >
          {children}
        </button>
      ) : (
        React.Children.map(children, child =>
          isValidElement<HTMLAttributes<HTMLElement> & { to?: unknown; href?: string }>(
            child,
          ) &&
          (child.type === 'button' ||
            child.type === 'a' ||
            child.props.to ||
            child.props.href)
            ? cloneElement(child, {
                role: child.props.role ?? 'menuitem',
                ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
                ...(isDisable ? { tabIndex: -1, 'aria-disabled': true } : {}),
              })
            : child,
        )
      )}
    </li>
  );
};

export { MenuItem };
